import {
  build_payment_template,
  build_people_template,
  build_template_profile,
  chips,
  item_section,
  timeline_section,
  with_default
} from './workflows.template_profile_helpers.js';

function build_supplier_onboarding_template(template) {
  return build_template_profile(template, {
    section: 'TEMPLATE · ONBOARDS',
    title: 'Supplier onboarding',
    subtitle: 'Application through to activation. Runs in OnboardOS; the activated supplier hands off to Complyon for monitoring.',
    status: 'Medium risk',
    status_class: 'bg-warning-subtle text-warning-emphasis',
    type: 'OnboardOS template',
    owner: 'Procurement',
    policy: 'POL-SCM',
    control: 'C-101',
    stage_count: 6,
    sections: (current_template) => [
      item_section('Governing rule', [
        { title: with_default(current_template?.policy, 'POL-SCM Procurement policy'), subtitle: 'Template policy link', meta: '', badge: '', icon: 'bi-file-earmark-text', icon_class: 'is-green' },
        { title: with_default(current_template?.control, 'C-101 No supplier paid without valid tax, banking & contract'), subtitle: 'Template control link', meta: '', badge: '', icon: 'bi-shield-check', icon_class: 'is-green' }
      ]),
      timeline_section(current_template, 6, [{ title: 'Application', subtitle: 'Applicant / SCM officer · SLA 1 day', meta: 'Company registration · Contact details', icon: 'bi-1-circle', icon_class: 'is-green', connected: true }, { title: 'Document collection', subtitle: 'SCM officer · SLA 3 days', meta: 'Tax clearance · BBBEE certificate · Banking · Insurance', icon: 'bi-2-circle', icon_class: 'is-green', connected: true }, { title: 'Verification', subtitle: 'Compliance · SLA 2 days', meta: 'CIPC check · Sanctions screen · Director check', icon: 'bi-3-circle', icon_class: 'is-slate', connected: true }, { title: 'Risk score', subtitle: 'Risk · SLA 1 day', meta: 'Risk scoring sheet', icon: 'bi-4-circle', icon_class: 'is-slate', connected: true }, { title: 'Approvals', subtitle: 'Finance / CFO · SLA 2 days · approval: CFO (above R 500k)', meta: 'DoA approval log', icon: 'bi-5-circle', icon_class: 'is-slate', connected: true }, { title: 'Activation', subtitle: 'OnboardOS · 1 day · approval: Accounting Officer', meta: 'Signed contract · Activation record', icon: 'bi-6-circle', icon_class: 'is-slate', connected: false }]),
      chips('Escalation path', ['SCM officer', 'SCM Manager', 'CFO (above R 500k)', 'Accounting Officer']),
      chips('Connects to', ['Onboarding handoff', 'Suppliers', 'Procurement', 'Risks', 'Evidence vault', 'Delegation of authority'])
    ]
  });
}

function build_employee_onboarding_template(template) {
  return build_template_profile(template, {
    section: 'TEMPLATE · ONBOARDS',
    title: 'Employee onboarding',
    subtitle: 'New-hire record to system-access approval. Runs in OnboardOS; the activated employee hands off to Complyon for ongoing compliance.',
    status: 'Medium risk',
    status_class: 'bg-warning-subtle text-warning-emphasis',
    type: 'OnboardOS template',
    owner: 'HR',
    policy: 'POL-08',
    control: 'C-203',
    stage_count: 5,
    sections: (current_template) => [
      item_section('Governing rule', [
        { title: with_default(current_template?.policy, 'POL-08 Code of conduct & ethics'), subtitle: 'Template policy link', meta: '', badge: '', icon: 'bi-file-earmark-text', icon_class: 'is-green' },
        { title: with_default(current_template?.control, 'C-203 Mandatory induction & POPIA training'), subtitle: 'Template control link', meta: '', badge: '', icon: 'bi-shield-check', icon_class: 'is-green' }
      ]),
      timeline_section(current_template, 5, [{ title: 'Employee record', subtitle: 'HR officer · SLA 1 day', meta: 'Signed contract · ID document', icon: 'bi-1-circle', icon_class: 'is-green', connected: true }, { title: 'Documents', subtitle: 'HR officer · SLA 2 days', meta: 'Qualifications · References · Bank details', icon: 'bi-2-circle', icon_class: 'is-green', connected: true }, { title: 'Declarations', subtitle: 'Compliance · SLA 1 day', meta: 'Conflict of interest · POPIA consent', icon: 'bi-3-circle', icon_class: 'is-green', connected: true }, { title: 'Training', subtitle: 'L&D · SLA 5 days', meta: 'Induction · POPIA · Security awareness', icon: 'bi-4-circle', icon_class: 'is-slate', connected: true }, { title: 'System access approval', subtitle: 'IT Security · SLA 1 day · approval: IT Security (privileged)', meta: 'Access request · Manager approval', icon: 'bi-5-circle', icon_class: 'is-slate', connected: false }]),
      chips('Escalation path', ['HR officer', 'HR Manager', 'IT Security (privileged access)', 'COO']),
      chips('Connects to', ['Onboarding handoff', 'Employees', 'Training', 'Conflicts', 'Evidence vault'])
    ]
  });
}

function build_policy_approval_template(template) {
  return build_template_profile(template, {
    section: 'TEMPLATE · COMPLYON',
    title: 'Policy approval',
    subtitle: 'Drafting, stakeholder review and governance approval through publication.',
    status: 'Medium risk',
    status_class: 'bg-warning-subtle text-warning-emphasis',
    type: 'Compliance template',
    owner: 'Compliance',
    policy: 'POL-META',
    control: 'C-501',
    stage_count: 6,
    sections: (current_template) => [
      timeline_section(current_template, 6, [{ title: 'Draft', subtitle: 'Policy owner · SLA 5 days', meta: 'Draft policy', icon: 'bi-1-circle', icon_class: 'is-green', connected: true }, { title: 'Review', subtitle: 'Stakeholders · SLA 5 days', meta: 'Comments and revisions', icon: 'bi-2-circle', icon_class: 'is-green', connected: true }, { title: 'Legal & compliance approval', subtitle: 'Legal / Compliance · SLA 3 days', meta: 'Approval log', icon: 'bi-3-circle', icon_class: 'is-green', connected: true }, { title: 'Board approval', subtitle: 'Board · next sitting', meta: 'Resolution', icon: 'bi-4-circle', icon_class: 'is-slate', connected: true }, { title: 'Publication', subtitle: 'Compliance · SLA 1 day', meta: 'Published version', icon: 'bi-5-circle', icon_class: 'is-slate', connected: true }, { title: 'Staff acknowledgement', subtitle: 'All staff · 30 days', meta: 'Acknowledgement register', icon: 'bi-6-circle', icon_class: 'is-slate', connected: false }]),
      chips('Escalation path', ['Policy owner', 'Legal & Compliance', 'EXCO', 'Board']),
      chips('Connects to', ['Policies', 'Audits', 'Evidence vault', 'Delegation of authority'])
    ]
  });
}

function build_incident_template(template) {
  return build_template_profile(template, {
    section: 'TEMPLATE · COMPLYON',
    title: 'Incident management',
    subtitle: 'Incident logged through notification to closure with corrective action.',
    status: 'High risk',
    status_class: 'bg-danger-subtle text-danger-emphasis',
    type: 'Compliance template',
    owner: 'Risk',
    policy: 'POL-IRM',
    control: 'C-216',
    stage_count: 5,
    sections: (current_template) => [
      timeline_section(current_template, 5, [{ title: 'Logged', subtitle: 'Reporter · SLA immediate', meta: 'Incident report', icon: 'bi-1-circle', icon_class: 'is-green', connected: true }, { title: 'Triage', subtitle: 'Case owner · SLA 1 day', meta: 'Severity assessment', icon: 'bi-2-circle', icon_class: 'is-green', connected: true }, { title: 'Investigation', subtitle: 'Investigator · SLA 5 days', meta: 'Findings · Root cause', icon: 'bi-3-circle', icon_class: 'is-green', connected: true }, { title: 'Corrective action', subtitle: 'Action owner · SLA 10 days', meta: 'Action plan · Completion evidence', icon: 'bi-4-circle', icon_class: 'is-slate', connected: true }, { title: 'Closure', subtitle: 'Compliance · SLA 2 days', meta: 'Closure report · Lessons learned', icon: 'bi-5-circle', icon_class: 'is-slate', connected: false }]),
      chips('Escalation path', ['First responder', 'Case owner', 'Compliance', 'EXCO (high severity)']),
      chips('Connects to', ['Incidents', 'Risks', 'Audits', 'Evidence vault', 'Data privacy'])
    ]
  });
}

function build_audit_finding_template(template) {
  return build_template_profile(template, {
    section: 'TEMPLATE · COMPLYON',
    title: 'Audit finding closure',
    subtitle: 'Finding logged through internal audit review to closure.',
    status: 'High risk',
    status_class: 'bg-danger-subtle text-danger-emphasis',
    type: 'Compliance template',
    owner: 'Compliance',
    policy: 'POL-IA',
    control: 'C-118',
    stage_count: 6,
    sections: (current_template) => [
      timeline_section(current_template, 6, [{ title: 'Finding logged', subtitle: 'Internal Audit · SLA 1 day', meta: 'Finding detail', icon: 'bi-1-circle', icon_class: 'is-green', connected: true }, { title: 'Owner assigned', subtitle: 'Management · SLA 2 days', meta: 'Owner acceptance', icon: 'bi-2-circle', icon_class: 'is-green', connected: true }, { title: 'Action plan', subtitle: 'Finding owner · SLA 5 days', meta: 'Remediation plan', icon: 'bi-3-circle', icon_class: 'is-green', connected: true }, { title: 'Evidence', subtitle: 'Finding owner · SLA per plan', meta: 'Remediation evidence', icon: 'bi-4-circle', icon_class: 'is-slate', connected: true }, { title: 'Internal audit review', subtitle: 'Internal Audit · SLA 3 days', meta: 'Verification', icon: 'bi-5-circle', icon_class: 'is-slate', connected: true }, { title: 'Closure', subtitle: 'Audit Committee · next sitting', meta: 'Closure sign-off', icon: 'bi-6-circle', icon_class: 'is-slate', connected: false }]),
      chips('Escalation path', ['Finding owner', 'Manager', 'Internal Audit', 'Audit Committee']),
      chips('Connects to', ['Audits', 'Risks', 'Procurement', 'Evidence vault', 'Delegation of authority'])
    ]
  });
}

function build_contract_approval_template(template) {
  return build_template_profile(template, {
    section: 'TEMPLATE · COMPLYON',
    title: 'Contract approval',
    subtitle: 'Upload through signature, then renewal tracking. Orchestrated in Complyon’s contracts module.',
    status: 'High risk',
    status_class: 'bg-danger-subtle text-danger-emphasis',
    type: 'Compliance template',
    owner: 'Legal',
    policy: 'POL-DOA',
    control: 'C-103',
    stage_count: 6,
    sections: (current_template) => [
      timeline_section(current_template, 6, [{ title: 'Upload', subtitle: 'Contract owner · SLA 1 day', meta: 'Draft contract', icon: 'bi-1-circle', icon_class: 'is-green', connected: true }, { title: 'Legal review', subtitle: 'Legal · SLA 3 days', meta: 'Legal markup', icon: 'bi-2-circle', icon_class: 'is-green', connected: true }, { title: 'Finance review', subtitle: 'Finance · SLA 2 days', meta: 'Budget confirmation', icon: 'bi-3-circle', icon_class: 'is-green', connected: true }, { title: 'Authority approval', subtitle: 'CIO / Board · SLA Per DoA', meta: 'DoA approval', icon: 'bi-4-circle', icon_class: 'is-green', connected: true }, { title: 'Signature', subtitle: 'Signatory · SLA 2 days', meta: 'Signed contract', icon: 'bi-5-circle', icon_class: 'is-slate', connected: true }, { title: 'Renewal tracking', subtitle: 'Compliance · SLA Ongoing', meta: 'Renewal alerts', icon: 'bi-6-circle', icon_class: 'is-slate', connected: false }]),
      chips('Escalation path', ['Contract owner', 'Legal', 'Finance', 'CIO/Board (by value)']),
      chips('Connects to', ['Contracts', 'Finance', 'Delegation of authority', 'Suppliers', 'Evidence vault'])
    ]
  });
}

const template_profile_matchers = [
  { match: (name) => name.includes('supplier onboarding'), build: build_supplier_onboarding_template },
  { match: (name) => name.includes('employee onboarding'), build: build_employee_onboarding_template },
  { match: (name) => name.includes('policy approval'), build: build_policy_approval_template },
  { match: (name) => name.includes('incident'), build: build_incident_template },
  { match: (name) => name.includes('audit finding'), build: build_audit_finding_template },
  { match: (name) => name.includes('contract approval'), build: build_contract_approval_template },
  { match: (name) => name.includes('payment exception'), build: (template) => build_payment_template(template, 'payment_exception') },
  { match: (name) => name.includes('supplier revalidation'), build: (template) => build_payment_template(template, 'supplier_revalidation') },
  { match: (name) => name.includes('training non-compliance'), build: (template) => build_people_template(template, 'training_non_compliance') },
  { match: (name) => name.includes('conflict of interest'), build: (template) => build_people_template(template, 'conflict_of_interest') }
];

export function get_template_profile(template) {
  const name = String(template?.name || '').toLowerCase();
  if (!name) return null;

  const matched_template = template_profile_matchers.find((matcher) => matcher.match(name));
  if (!matched_template) return null;
  const profile = matched_template.build(template);
  return profile;
}
